import * as React from "react";

interface CopilotContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  pendingPrompt: string | null;
  ask: (prompt: string) => void;
  clearPendingPrompt: () => void;
}

const CopilotContext = React.createContext<CopilotContextValue | undefined>(undefined);

export function CopilotProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const [pendingPrompt, setPendingPrompt] = React.useState<string | null>(null);

  const toggle = React.useCallback(() => setOpen((o) => !o), []);
  const ask = React.useCallback((prompt: string) => {
    setPendingPrompt(prompt);
    setOpen(true);
  }, []);
  const clearPendingPrompt = React.useCallback(() => setPendingPrompt(null), []);

  const value = React.useMemo(
    () => ({ open, setOpen, toggle, pendingPrompt, ask, clearPendingPrompt }),
    [open, toggle, pendingPrompt, ask, clearPendingPrompt],
  );

  return <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>;
}

export function useCopilot() {
  const ctx = React.useContext(CopilotContext);
  if (!ctx) throw new Error("useCopilot must be used within CopilotProvider");
  return ctx;
}
